import { saveAs } from 'file-saver';
import { getInstance } from '../index';
import {
  getApiEmbedUwAssets,
  buildUrlNbj1ApplicationAnswers,
  downloadFile,
  getFilenameFromResponseHeaders,
  apiBaseUrl,
} from '../../utilities/api/apiHelper';
import { setURLParams } from '../../utilities/application/urlHelper';

export function switchAPIVersion(baseUrl, version) {
  return baseUrl.replace(/v\d+$/, version);
}

class Application {
  constructor() {
    this.axios = getInstance();
  }

  async postCreateApplication() {
    return await this.axios.post(`${apiBaseUrl}/application`, {});
  }

  async postStartApplication(adviserId) {
    const params = [];
    if (adviserId) {
      params.push({ adviserId });
    }
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/create${setURLParams(
        params
      )}`,
      {}
    );
  }

  async postAddApplicant(data) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/${data.applicationId}/AddApplicant`,
      {
        applicationId: data.applicationId,
        applicant: {},
      }
    );
  }

  async postApplyApplication(applicationId) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/ApplyApplication`,
      {
        applicationId,
      }
    );
  }

  async getApplication(applicationId) {
    return await this.axios.get(`${apiBaseUrl}/application/${applicationId}`);
  }

  async getApplicationUnenriched(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/application/${applicationId}/unenriched`
    );
  }

  async postApplicantById(applicationId, data) {
    let answers = { [Object.keys(data)[1]]: Object.values(data)[1] };
    if (Object.keys(data).length > 2) {
      Object.keys(data).forEach((key, index) => {
        if (index > 1) {
          answers[Object.keys(data)[index]] = Object.values(data)[index];
        }
      });
    }

    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/applicant/${
        data.id
      }${getApiEmbedUwAssets()}`,
      answers
    );
  }

  async patchApplicantById(applicationId, data) {
    let answers = { [Object.keys(data)[1]]: Object.values(data)[1] };
    if (Object.keys(data).length > 2) {
      Object.keys(data).forEach((key, index) => {
        if (index > 1) {
          answers[Object.keys(data)[index]] = Object.values(data)[index];
        }
      });
    }

    return await this.axios.patch(
      `${apiBaseUrl}/application/${applicationId}/applicant/${
        data.id
      }${getApiEmbedUwAssets()}`,
      answers
    );
  }

  async patchApplicantName(applicantId, data) {
    return await this.axios.patch(
      `${apiBaseUrl}/backendforfrontend/applicant/name`,
      {
        applicantId,
        ...data,
      }
    );
  }

  async postCreateApplicantUnderwritingEnquiryByApplicantId({ applicantId }) {
    return await this.axios.post(
      `${apiBaseUrl}/underwriting/${applicantId}/start${getApiEmbedUwAssets()}`,
      { answers: { ENQUIRY_TYPE: 'Full' } }
    );
  }

  async fetchApplicantUnderwritingEnquiryByApplicantId({ applicantId }) {
    return await this.axios.get(
      `${apiBaseUrl}/underwriting/${applicantId}${getApiEmbedUwAssets()}`,
      {}
    );
  }

  async postApplicationEnquiryById(applicantId, answers, allAnswers, isQi) {
    const payload = {
      answers: { ...allAnswers, ...answers },
    };

    const url = isQi ? 'underwriting/' : 'actions/provideanswers/';

    return await this.axios.post(
      `${apiBaseUrl}/${url}${applicantId}${getApiEmbedUwAssets()}`,
      { ...payload }
    );
  }

  async fetchApplicationById(applicantionId) {
    return await this.axios.get(
      `${apiBaseUrl}/application/${applicantionId}`,
      {}
    );
  }

  async getApplicantCovers(applicantId) {
    return await this.axios.get(
      `${apiBaseUrl}/applicant/${applicantId}/covers`
    );
  }

  async postApplicantCover(applicationId, data) {
    let answers = { [Object.keys(data)[1]]: Object.values(data)[1] };
    if (Object.keys(data).length > 2) {
      Object.keys(data).forEach((key, index) => {
        if (index > 1) {
          answers[Object.keys(data)[index]] =
            Object.values(data)[index] !== ''
              ? Object.values(data)[index]
              : null;
        }
      });
    }

    return await this.axios.post(`${apiBaseUrl}/actions/MutateCovers/add`, {
      applicationId,
      applicantId: data.applicantId,
      cover: answers,
    });
  }

  async patchApplicantCover(applicationId, data) {
    let answers = { [Object.keys(data)[1]]: Object.values(data)[1] };
    if (Object.keys(data).length > 2) {
      Object.keys(data).forEach((key, index) => {
        if (index > 1) {
          answers[Object.keys(data)[index]] =
            Object.values(data)[index] !== ''
              ? Object.values(data)[index]
              : null;
        }
      });
    }

    return await this.axios.patch(`${apiBaseUrl}/actions/MutateCovers/edit`, {
      applicationId,
      applicantId: data.applicantId,
      cover: answers,
    });
  }

  async postApplicantCoverIsPayoutPlannerUsed(
    applicantId,
    coverId,
    isPayoutPlannerUsed
  ) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/cover/isPayoutPlannerUsed`,
      {
        applicantId,
        coverId,
        isPayoutPlannerUsed,
      }
    );
  }

  async deleteApplicantCover(applicationId, data) {
    let answers = { [Object.keys(data)[1]]: Object.values(data)[1] };
    if (Object.keys(data).length > 2) {
      Object.keys(data).forEach((key, index) => {
        if (index > 1) {
          answers[Object.keys(data)[index]] =
            Object.values(data)[index] !== ''
              ? Object.values(data)[index]
              : null;
        }
      });
    }

    return await this.axios.delete(
      `${apiBaseUrl}/actions/MutateCovers/remove`,
      {
        data: {
          applicationId,
          applicantId: data.applicantId,
          cover: answers,
        },
      }
    );
  }

  async getApplicationValidateCovers(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/application/${applicationId}/validatecovers`
    );
  }

  async getQuotePrice(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/application/${applicationId}/quotePrice`,
      {}
    );
  }

  async postAcceptPreUnderwritingDeclarationById(applicationId, applicantId) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/acceptPreUnderwritingDeclaration`,
      {}
    );
  }

  async postApplicationThirdPartyReference(id, thirdPartyRef) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/thirdPartyRef`,
      { id, thirdPartyRef }
    );
  }

  async postApplicationCommission(
    id,
    initialCommissionSacrificePercentage,
    initialIndemnityPercentage
  ) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/commission`,
      { id, initialCommissionSacrificePercentage, initialIndemnityPercentage }
    );
  }

  async postApplicationQuoteDate(applicationId, quoteValidUntilDate) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/quoteDate`,
      { applicationId, quoteValidUntilDate }
    );
  }

  async postApplicationMortgageRelated(id, isMortgageRelated) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/isMortgageRelated`,
      { id, isMortgageRelated }
    );
  }

  async deleteApplicantById(applicationId, applicantId) {
    return await this.axios.delete(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}`,
      {}
    );
  }

  async postApplicationIsAdvisedSale(id, isAdvisedSale) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/isAdvisedSale`,
      { id, isAdvisedSale }
    );
  }

  async postApplicationPolicyStartDate(
    id,
    policyStartDate,
    policyStartDateOption
  ) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/policyStartDate`,
      { id, policyStartDate, policyStartDateOption }
    );
  }

  async postActivateApplication(applicationId) {
    return await this.axios.post(
      `${apiBaseUrl}/actions/activateApplication/${applicationId}`,
      {}
    );
  }

  async getApplicationStatus(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/statuses/${applicationId}`
    );
  }

  async postSameAddressApplicantId(applicantId, sameAddressApplicantId) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/applicant/sameAddressApplicantId`,
      { applicantId, sameAddressApplicantId }
    );
  }

  async getApplicationWithDecisions(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/applicationWithDecisions/${applicationId}`
    );
  }

  async fetchApplicantContact(applicationId, applicantId) {
    return await this.axios.get(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/contact`
    );
  }

  async postApplicantContact(applicationId, applicantId, data) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/contact`,
      {
        ...data,
      }
    );
  }

  async postApplicationChild(applicationId, data) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/children`,
      {
        ...data,
      }
    );
  }

  async putApplicationChild(applicationId, id, data) {
    return await this.axios.put(
      `${apiBaseUrl}/application/${applicationId}/children/${id}`,
      {
        ...data,
      }
    );
  }

  async deleteApplicationChild(applicationId, id) {
    return await this.axios.delete(
      `${apiBaseUrl}/application/${applicationId}/children/${id}`,
      {}
    );
  }

  async getBankDetails(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/application/${applicationId}/bankDetails`
    );
  }

  async postBankDetails(applicationId, bankDetails) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/bankDetails`,
      { bankDetails }
    );
  }

  async postValidateBankDetails(applicationId) {
    return await this.axios.post(
      `${switchAPIVersion(
        apiBaseUrl,
        'v2'
      )}/backendforfrontend/bankvalidation/${applicationId}`,
      {}
    );
  }

  async postOverrideBankValidation(applicationId) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/bankValidation/${applicationId}/override`,
      {}
    );
  }

  async postApplicationCoverBeneficiary(applicationId, data) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/candidateBeneficiaries`,
      {
        ...data,
      }
    );
  }

  async putApplicationCoverBeneficiary(applicationId, beneficiaryId, data) {
    return await this.axios.put(
      `${apiBaseUrl}/application/${applicationId}/candidateBeneficiary/${beneficiaryId}`,
      {
        ...data,
      }
    );
  }

  async postApplicationCoverBeneficiaryAllocation(
    applicationId,
    applicantId,
    coverId,
    referenceId,
    type,
    percent
  ) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/cover/${coverId}/benefit-allocations`,
      {
        referenceId,
        percent,
        type,
      }
    );
  }

  async putApplicationCoverBeneficiaryAllocation(
    applicationId,
    applicantId,
    coverId,
    beneficiaryAllocationId,
    referenceId,
    type,
    percent
  ) {
    return await this.axios.put(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/cover/${coverId}/benefit-allocations/${beneficiaryAllocationId}`,
      {
        referenceId,
        type,
        percent,
      }
    );
  }

  async deleteApplicationCoverBeneficiaryAllocation(
    applicationId,
    applicantId,
    coverId,
    beneficiaryAllocationId
  ) {
    return await this.axios.delete(
      `${apiBaseUrl}/application/${applicationId}/applicant/${applicantId}/cover/${coverId}/benefit-allocations/${beneficiaryAllocationId}`,
      {}
    );
  }

  async postApplicationDecision(applicantId) {
    return await this.axios.post(
      `${apiBaseUrl}/decision/${applicantId}/optoutoflogging`,
      {}
    );
  }

  async postApplicationQiDecision(applicantId) {
    return await this.axios.post(
      `${apiBaseUrl}/decision/qi/${applicantId}/risk`,
      {}
    );
  }

  async _nbj1OpenApplicationAnswersDocument(
    applicationId,
    interviewId,
    interviewToken,
    adviserId
  ) {
    const documentUrl = buildUrlNbj1ApplicationAnswers(
      applicationId,
      interviewId,
      interviewToken,
      adviserId
    );
    const url = `${apiBaseUrl}/${documentUrl}`;
    return downloadFile(this.axios, url, saveAs);
  }

  async _nbj2OpenApplicationAnswersDocument(applicationId, applicantId) {
    try {
      const response = await this.axios.post(
        `${apiBaseUrl}/backendforfrontend/createLatestApplicantAnswersDocument`,
        { applicationId, applicantId },
        { responseType: 'blob' }
      );
      const fileName = getFilenameFromResponseHeaders(response.headers);
      return saveAs(response.data, fileName);
    } catch (error) {
      console.log('Error getting document', error);
    }
  }

  async openApplicationAnswersDocument(
    applicationId,
    applicantId,
    interviewId,
    interviewToken,
    adviserId
  ) {
    if (interviewId && interviewToken) {
      return this._nbj1OpenApplicationAnswersDocument(
        applicationId,
        interviewId,
        interviewToken,
        adviserId
      );
    } else {
      return this._nbj2OpenApplicationAnswersDocument(
        applicationId,
        applicantId
      );
    }
  }

  async openDecisionDocument(
    applicationId,
    interviewId,
    interviewToken,
    adviserId
  ) {
    let url;
    if (interviewId && interviewToken) {
      // NBJ1 doc
      url = `${apiBaseUrl}/document/interview/${interviewId}/${interviewToken}/decision?fileName=${applicationId}-decision`;
      url = !adviserId ? url : `${url}&adviserId=${adviserId}`;
    } else {
      // NBJ2 doc
      url = `${apiBaseUrl}/backendforfrontend/application/${applicationId}/document/decision`;
    }
    return downloadFile(this.axios, url, saveAs);
  }

  async openDocumentByUrl(url) {
    window.open(url, '_blank');
  }

  async getApplicationUnderwritingUpdates(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/underwritingupdates/${applicationId}`
    );
  }

  async postHasUnbornChildren(applicationId, hasUnbornChildren) {
    return await this.axios.post(
      `${apiBaseUrl}/application/${applicationId}/hasUnbornChildren`,
      { hasUnbornChildren }
    );
  }

  async postPortalApply(portalId) {
    return await this.axios.post(`${apiBaseUrl}/portal/apply/${portalId}`, {});
  }

  async patchUpsellApplicantCover(applicationId, applicantId, cover) {
    return await this.axios.patch(`${apiBaseUrl}/actions/upsellcover`, {
      applicationId,
      applicantId,
      cover,
    });
  }

  async postUnlockQuoteEdit(applicationId) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/unlockquoteedit/${applicationId}`,
      {}
    );
  }

  async patchContactDetails(applicantId, data) {
    return await this.axios.patch(
      `${apiBaseUrl}/backendforfrontend/applicant/contact`,
      {
        applicantId,
        ...data,
      }
    );
  }

  async getIsTermExtensible(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/application/${applicationId}/istermextensible`
    );
  }

  async triggerDeclarationOfHealthQuestions(applicationId) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/application/${applicationId}/triggerDeclarationOfHealthQuestions`
    );
  }

  async completeTermExtension(applicationId, applicants) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/${applicationId}/completeTermExtension`,
      {
        applicationId,
        applicants,
      }
    );
  }

  async hasFailedTermExtension(applicationId) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/application/hasFailedTermExtension`,
      {
        id: applicationId,
      }
    );
  }

  async getSurgeries(searchText) {
    return await this.axios.get(
      `${apiBaseUrl}/backendforfrontend/examiner/lookup?search=${searchText}`
    );
  }

  async postAmraConsentGranted(applicantId, isAmraConsentGranted) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/applicant/isAmraConsentGranted`,
      {
        applicantId,
        isAmraConsentGranted,
      }
    );
  }

  async postViewMedicalReportsRequested(
    applicantId,
    isViewMedicalReportsRequested
  ) {
    return await this.axios.post(
      `${apiBaseUrl}/backendforfrontend/applicant/isViewMedicalReportsRequested`,
      {
        applicantId,
        isViewMedicalReportsRequested,
      }
    );
  }

  async patchApplicantExaminerDetails(applicantId, data) {
    return await this.axios.patch(
      `${apiBaseUrl}/backendforfrontend/applicant/examiner`,
      {
        applicantId,
        ...data,
        isExaminerDetailsValid:
          data.isExaminerDetailsValid === true ? 'true' : 'false',
      }
    );
  }
}

export const getKeyFactsDocumentUrl = async (quoteDate, applicationId) => {
  try {
    const response = await application.axios.get(
      `${apiBaseUrl}/backendforfrontend/${applicationId}/keyfacts/${quoteDate}`
    );
    return response.data.data.uri;
  } catch (error) {
    console.log('Error getting document', error);
  }
};

export const getTermsAndConditionsDocumentUrl = async (quoteDate) => {
  try {
    const response = await application.axios.get(
      `${apiBaseUrl}/backendforfrontend/termsandconditions/${quoteDate}`
    );
    return response.data.data.uri;
  } catch (error) {
    console.log('Error getting document', error);
  }
};

const application = new Application();
export default application;
